import React from "react";
import { X, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";

import "./PostalCodeCoverageSelector.css";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

/* =========================================================
   GOOGLE MAPS LOADER
   Uses the same global promise + script id as the existing
   LEG-UP Google Maps components.
========================================================= */

function loadGoogleMaps(apiKey) {
  if (!apiKey || typeof window === "undefined") {
    return Promise.resolve(null);
  }

  /*
   * Google Maps + Places already available.
   */
  if (window.google?.maps?.places) {
    return Promise.resolve(window.google);
  }

  /*
   * Another LEG-UP component is already loading Maps.
   */
  if (window.__legupGoogleMapsPromise) {
    return window.__legupGoogleMapsPromise;
  }

  window.__legupGoogleMapsPromise = new Promise((resolve, reject) => {
    const scriptId = "legup-google-maps-script";

    const existingScript = document.getElementById(scriptId);

    function resolveWhenReady() {
      if (window.google?.maps) {
        resolve(window.google);
      } else {
        reject(new Error("Google Maps API is unavailable."));
      }
    }

    /*
     * Script already exists but is still loading.
     */
    if (existingScript) {
      if (window.google?.maps) {
        resolve(window.google);
        return;
      }

      existingScript.addEventListener("load", resolveWhenReady, {
        once: true,
      });

      existingScript.addEventListener("error", reject, {
        once: true,
      });

      return;
    }

    /*
     * First LEG-UP component requesting Google Maps.
     */
    const script = document.createElement("script");

    script.id = scriptId;

    script.src =
      "https://maps.googleapis.com/maps/api/js" +
      `?key=${encodeURIComponent(apiKey)}` +
      "&libraries=places&v=weekly";

    script.async = true;
    script.defer = true;

    script.onload = resolveWhenReady;

    script.onerror = () => {
      /*
       * Important:
       * reset the cached promise so another attempt
       * can be made later.
       */
      window.__legupGoogleMapsPromise = null;

      reject(new Error("Failed to load Google Maps."));
    };

    document.head.appendChild(script);
  });

  return window.__legupGoogleMapsPromise;
}

/* =========================================================
   POSTAL CODE COVERAGE SELECTOR
========================================================= */

export default function PostalCodeCoverageSelector({
  countryCode,
  value = [],
  onChange,
  error,
}) {
  const { t } = useTranslation("oss-registration-step1");

  const [query, setQuery] = React.useState("");

  const [suggestions, setSuggestions] = React.useState([]);

  const [loading, setLoading] = React.useState(false);

  const [mapsError, setMapsError] = React.useState("");
  const sessionTokenRef = React.useRef(null);

  const requestIdRef = React.useRef(0);

  const searchTimerRef = React.useRef(null);

  /* =======================================================
     RESET WHEN COUNTRY CHANGES
  ======================================================= */

  React.useEffect(() => {
    sessionTokenRef.current = null;

    requestIdRef.current += 1;

    if (searchTimerRef.current) {
      window.clearTimeout(searchTimerRef.current);

      searchTimerRef.current = null;
    }

    setQuery("");
    setSuggestions([]);
    setMapsError("");
    setLoading(false);
  }, [countryCode]);

  /* =======================================================
     GET / CREATE AUTOCOMPLETE SESSION TOKEN
  ======================================================= */

  async function ensureSessionToken() {
    const google = await loadGoogleMaps(GOOGLE_MAPS_API_KEY);

    if (!google?.maps) {
      throw new Error(
        t("postalCoverage.mapsUnavailable", "Google Maps is unavailable."),
      );
    }

    const { AutocompleteSessionToken } =
      await google.maps.importLibrary("places");

    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new AutocompleteSessionToken();
    }

    return sessionTokenRef.current;
  }

  /* =======================================================
     SEARCH
  ======================================================= */

  async function searchPostalCodes(input) {
    setMapsError("");

    if (!countryCode || input.trim().length < 2) {
      setSuggestions([]);
      return;
    }

    /*
     * No API key configured.
     */
    if (!GOOGLE_MAPS_API_KEY) {
      setMapsError(
        t(
          "postalCoverage.mapsKeyMissing",
          "Google Maps is unavailable because the API key is not configured.",
        ),
      );

      return;
    }

    const currentRequestId = ++requestIdRef.current;

    try {
      setLoading(true);

      const google = await loadGoogleMaps(GOOGLE_MAPS_API_KEY);

      if (!google?.maps) {
        throw new Error("Google Maps is unavailable.");
      }

      const { AutocompleteSuggestion } =
        await google.maps.importLibrary("places");

      const sessionToken = await ensureSessionToken();

      const { suggestions: results = [] } =
        await AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: input.trim(),

          includedRegionCodes: [countryCode.toLowerCase()],

          includedPrimaryTypes: ["(regions)"],

          sessionToken,
        });

      if (currentRequestId !== requestIdRef.current) {
        return;
      }

      const postalCodeResults = results.filter((item) => {
        const prediction = item.placePrediction;

        if (!prediction) {
          return false;
        }

        return prediction.types?.includes("postal_code");
      });

      console.log(
        "Google raw predictions:",
        results.map((item) => ({
          text: item.placePrediction?.text?.toString(),
          types: item.placePrediction?.types,
        })),
      );

      console.log("Postal code predictions:", postalCodeResults);

      setSuggestions(postalCodeResults);
    } catch (error) {
      console.error("Postal code autocomplete failed:", error);

      setSuggestions([]);

      setMapsError(
        t(
          "postalCoverage.searchError",
          "Postal codes could not be loaded. Please try again.",
        ),
      );
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }

  /* =======================================================
   QUERY CHANGE
   ======================================================= */

  function handleQueryChange(event) {
    const nextQuery = event.target.value;

    setQuery(nextQuery);

    setMapsError("");

    if (searchTimerRef.current) {
      window.clearTimeout(searchTimerRef.current);
    }

    if (!countryCode || nextQuery.trim().length < 2) {
      requestIdRef.current += 1;

      setSuggestions([]);
      setLoading(false);

      return;
    }

    setLoading(true);

    searchTimerRef.current = window.setTimeout(() => {
      searchPostalCodes(nextQuery);
    }, 280);
  }

  /* =======================================================
     SELECT SUGGESTION
  ======================================================= */

  async function selectSuggestion(suggestion) {
    try {
      setMapsError("");

      const prediction = suggestion.placePrediction;

      if (!prediction) {
        return;
      }

      const place = prediction.toPlace();

      await place.fetchFields({
        fields: ["addressComponents", "formattedAddress"],
      });

      const postalComponent = place.addressComponents?.find((component) =>
        component.types.includes("postal_code"),
      );

      const countryComponent = place.addressComponents?.find((component) =>
        component.types.includes("country"),
      );

      const postalCode = postalComponent?.longText || "";

      const selectedCountry = countryComponent?.shortText || "";

      if (!postalCode) {
        setMapsError(
          t(
            "postalCoverage.notPostalCode",
            "Please select a postal code rather than a city or region.",
          ),
        );

        return;
      }

      if (
        selectedCountry &&
        selectedCountry.toUpperCase() !== countryCode.toUpperCase()
      ) {
        setMapsError(
          t(
            "postalCoverage.countryMismatch",
            "The selected postal code does not belong to the selected country.",
          ),
        );

        return;
      }

      const normalizedCountry = countryCode.toUpperCase();

      const alreadySelected = value.some(
        (item) =>
          item.country_code === normalizedCountry &&
          item.postal_code === postalCode,
      );

      if (!alreadySelected) {
        onChange([
          ...value,

          {
            country_code: normalizedCountry,

            postal_code: postalCode,

            place_id: place.id || "",
            label: place.formattedAddress || postalCode,
          },
        ]);
      }

      setQuery("");
      setSuggestions([]);

      sessionTokenRef.current = null;
    } catch (error) {
      console.error("Could not resolve postal code:", error);

      setMapsError(
        t(
          "postalCoverage.selectionError",
          "The selected postal code could not be loaded.",
        ),
      );
    }
  }

  /* =======================================================
     REMOVE
  ======================================================= */

  function removePostalCode(postalCode) {
    onChange(value.filter((item) => item.postal_code !== postalCode));
  }

  React.useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        window.clearTimeout(searchTimerRef.current);
      }

      requestIdRef.current += 1;
    };
  }, []);

  /* =======================================================
   RENDER
   ======================================================= */

  return (
    <div className="oss-postal-coverage">
      {/* ===================================================
        AUTOCOMPLETE
        =================================================== */}

      <div className="oss-postal-coverage__autocomplete">
        <div className="oss-postal-coverage__search">
          <MapPin size={18} aria-hidden="true" />

          <input
            type="text"
            value={query}
            onChange={handleQueryChange}
            placeholder={
              countryCode
                ? t("postalCoverage.searchPlaceholder", "Search postal code")
                : t(
                    "postalCoverage.selectCountryFirst",
                    "Select a country first",
                  )
            }
            disabled={!countryCode}
            inputMode="numeric"
            autoComplete="off"
            aria-label={t("postalCoverage.searchLabel", "Search postal codes")}
            aria-expanded={loading || suggestions.length > 0}
          />
        </div>

        {/* ===============================================
          FLOATING DROPDOWN
          =============================================== */}

        {loading || suggestions.length > 0 ? (
          <div className="oss-postal-coverage__dropdown">
            {loading ? (
              <div className="oss-postal-coverage__status">
                <span className="oss-postal-coverage__spinner" />

                {t("postalCoverage.searching", "Searching...")}
              </div>
            ) : null}

            {!loading && suggestions.length > 0 ? (
              <div
                className="oss-postal-coverage__suggestions"
                role="listbox"
                aria-label={t(
                  "postalCoverage.suggestionsLabel",
                  "Postal code suggestions",
                )}
              >
                {suggestions.map((suggestion, index) => {
                  const prediction = suggestion.placePrediction;

                  return (
                    <button
                      key={prediction?.placeId || index}
                      type="button"
                      role="option"
                      /*
                       * Keep focus on the search field
                       * until the click is processed.
                       * Prevents another visual jump.
                       */
                      onMouseDown={(event) => {
                        event.preventDefault();
                      }}
                      onClick={() => selectSuggestion(suggestion)}
                    >
                      <MapPin size={15} aria-hidden="true" />

                      <span>{prediction?.text?.toString()}</span>
                    </button>
                  );
                })}
              </div>
            ) : null}

            {!loading &&
            query.trim().length >= 2 &&
            suggestions.length === 0 ? (
              <div className="oss-postal-coverage__empty">
                {t("postalCoverage.noResults", "No postal codes found.")}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* ===================================================
        ERROR
        =================================================== */}

      {mapsError ? (
        <span className="oss-step1-field-error">{mapsError}</span>
      ) : null}

      {/* ===================================================
        SELECTED POSTAL CODES
        =================================================== */}

      {value.length > 0 ? (
        <>
          <div className="oss-postal-coverage__selected">
            {value.map((item) => (
              <span
                key={`${item.country_code}-` + `${item.postal_code}`}
                className="oss-postal-chip"
              >
                {item.postal_code}

                <button
                  type="button"
                  onClick={() => removePostalCode(item.postal_code)}
                  aria-label={t("postalCoverage.removePostalCode", {
                    postalCode: item.postal_code,

                    defaultValue: `Remove postal code ${item.postal_code}`,
                  })}
                >
                  <X size={14} aria-hidden="true" />
                </button>
              </span>
            ))}
          </div>

          <div className="oss-postal-coverage__count">
            {t("postalCoverage.selectedCount", {
              count: value.length,

              defaultValue: "{{count}} postal code selected",
            })}
          </div>
        </>
      ) : null}

      {/* ===================================================
        FORM ERROR
        =================================================== */}

      {error ? <span className="oss-step1-field-error">{error}</span> : null}
    </div>
  );
}
